/**
 * SeaMonster Studios — dropdown edge guard.
 *
 * Only loaded when the header menu block's "Submenu layout" setting is
 * "Dropdown" (see `blocks/_header-menu.liquid`).
 *
 * Traditional dropdowns are anchored to the left edge of their top-level item.
 * For an item near the right edge of the viewport that would push the panel off
 * screen, so this flips the panel to right-aligned by setting
 * `data-dropdown-align="end"` on the list item; `assets/custom.css` section 6
 * does the rest.
 *
 * Measuring works while the panel is closed because Horizon hides it with
 * `visibility: hidden`, which still produces a layout box. Nothing here opens,
 * closes or otherwise touches menu behaviour — `header-menu.js` still owns that.
 */

/** Breathing room to leave between the panel and the viewport edge, in px. */
const VIEWPORT_MARGIN = 16;

/**
 * Flip any dropdown that would overflow the viewport's inline end.
 * @param {Element} menu - A `header-menu` element in dropdown mode.
 */
function alignDropdowns(menu) {
  const items = menu.querySelectorAll('.menu-list__list-item:not([slot="overflow"]):not([slot="more"])');
  const viewportWidth = document.documentElement.clientWidth;

  for (const item of items) {
    if (!(item instanceof HTMLElement)) continue;

    const panel = item.querySelector(':scope > .menu-list__submenu');
    if (!panel) continue;

    // Measure from the un-flipped position, otherwise the previous flip decides
    // the next one.
    delete item.dataset.dropdownAlign;

    const itemStart = item.getBoundingClientRect().left;
    const panelWidth = panel.getBoundingClientRect().width;

    if (itemStart + panelWidth > viewportWidth - VIEWPORT_MARGIN) {
      item.dataset.dropdownAlign = 'end';
    }
  }
}

function alignAllDropdowns() {
  const menus = document.querySelectorAll('header-menu[data-submenu-layout="dropdown"]');
  for (const menu of menus) alignDropdowns(menu);
}

/** @type {number | undefined} */
let resizeFrame;

function scheduleAlign() {
  if (resizeFrame) cancelAnimationFrame(resizeFrame);
  resizeFrame = requestAnimationFrame(() => {
    resizeFrame = undefined;
    alignAllDropdowns();
  });
}

scheduleAlign();
window.addEventListener('resize', scheduleAlign);
window.addEventListener('load', scheduleAlign);
document.addEventListener('shopify:section:load', scheduleAlign);
document.fonts?.ready.then(scheduleAlign);
